import React, { useEffect, useState } from "react";
import { TopBar } from "@/components/TopBar";
import { EmptyState } from "@/components/EmptyState";
import { MainAction } from "@/components/MainAction";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useBackButton } from "@/lib/hooks";
import { formatUzs } from "@/lib/format";
import { ApiError, bookEvent, cancelBooking, fetchEvents, fetchMyBookings, mediaUrl, type EventBooking, type ShopEvent } from "@/lib/api";
import { getTelegramUser, haptic } from "@/lib/telegram";

const when = (iso: string, locale: string) =>
  new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "ru-RU", { weekday: "short", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Tashkent" }).format(new Date(iso));

/** Cuppings & masterclasses (only when FEATURE_EVENTS is on). */
export function EventsPage() {
  const { t, locale } = useI18n();
  const { goBack, navigate } = useRouter();
  useBackButton(goBack);
  const u = getTelegramUser();
  const [events, setEvents] = useState<ShopEvent[] | null>(null);
  const [mine, setMine] = useState<EventBooking[]>([]);
  const [booking, setBooking] = useState<ShopEvent | null>(null);
  const [form, setForm] = useState({ fullName: [u?.first_name, u?.last_name].filter(Boolean).join(" "), phone: "", seats: 1 });
  const [error, setError] = useState<string | null>(null);
  const load = () => {
    fetchEvents().then(setEvents).catch(() => setEvents([]));
    fetchMyBookings().then(setMine).catch(() => setMine([]));
  };
  useEffect(load, [locale]);

  return (
    <>
      <TopBar title={t("events.title")} onBack={goBack} />
      <main className="page page--no-nav">
        {mine.length > 0 && (
          <>
            <div className="section-heading"><span className="diamond" />{t("events.mine")}</div>
            {mine.map((b) => (
              <button key={b.id} className="card biz-row" onClick={() => navigate({ name: "ticket", id: b.id })}>
                <span><b>{b.event.title}</b><small>{when(b.event.startsAt, locale)}</small></span>
                <span>🎟</span>
              </button>
            ))}
          </>
        )}
        {events === null ? (
          <p>{t("common.loading")}</p>
        ) : events.length === 0 ? (
          <EmptyState art="generic" title={t("events.empty")} />
        ) : (
          events.map((e) => (
            <div key={e.id} className="event-card">
              {e.coverImage && <img src={mediaUrl(e.coverImage, 800)} alt="" />}
              <div style={{ padding: 16 }}>
                <span className="eyebrow">{when(e.startsAt, locale)} · {e.durationMin} {t("events.min")}</span>
                <h3>{e.title}</h3>
                <p>{e.description}</p>
                <small>📍 {e.place} · {e.seatsLeft > 0 ? t("events.left", { n: e.seatsLeft }) : t("events.full")}</small>
                <button className="btn btn--primary" style={{ marginTop: 12 }} disabled={e.seatsLeft <= 0} onClick={() => { setBooking(e); setError(null); }}>
                  {t("events.book")} · {e.priceUzs ? formatUzs(e.priceUzs, locale) : t("events.free")}
                </button>
              </div>
            </div>
          ))
        )}
      </main>
      {booking && (
        <div className="sheet-overlay" onClick={() => setBooking(null)}>
          <div className="sheet" onClick={(ev) => ev.stopPropagation()}>
            <div className="sheet__handle" />
            <h2 className="sheet__title">{booking.title}</h2>
            <p className="sheet__text">{when(booking.startsAt, locale)} · {booking.place}{booking.priceUzs ? ` · ${t("events.payThere", { price: formatUzs(booking.priceUzs, locale) })}` : ""}</p>
            <div className="field"><label>{t("checkout.fullName")}</label><input value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} /></div>
            <div className="field"><label>{t("checkout.phone")}</label><input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+998 90 123 45 67" /></div>
            <div className="segmented" style={{ marginBottom: 12 }}>
              {[1, 2, 3, 4].map((n) => (
                <button key={n} className="chip" data-active={form.seats === n} disabled={n > booking.seatsLeft} onClick={() => setForm({ ...form, seats: n })}>{t("events.seats", { n })}</button>
              ))}
            </div>
            {error && <div className="form-error-banner">{error}</div>}
            <MainAction
              text={t("events.confirm")}
              onClick={async () => {
                try {
                  const b = await bookEvent(booking.id, form);
                  haptic("success");
                  setBooking(null);
                  navigate({ name: "ticket", id: b.id });
                } catch (err) {
                  setError(err instanceof ApiError ? err.message : t("common.error"));
                }
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}

/** Ticket with a QR code for the door. */
export function TicketPage({ id }: { id: string }) {
  const { t, locale } = useI18n();
  const { goBack, navigate } = useRouter();
  useBackButton(goBack);
  const [b, setB] = useState<EventBooking | null | undefined>(undefined);
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    fetchMyBookings().then((list) => setB(list.find((x) => x.id === id) ?? null)).catch(() => setB(null));
  }, [id]);
  useEffect(() => {
    if (!b) return;
    import("qrcode")
      .then((m) => m.toDataURL(`KMS-EVENT:${b.code}`, { width: 360, margin: 1, color: { dark: "#173561", light: "#ffffff" } }))
      .then(setQr)
      .catch(() => setQr(null));
  }, [b]);
  if (b === undefined) return (<><TopBar title={t("events.ticket")} onBack={goBack} /><main className="page page--no-nav">{t("common.loading")}</main></>);
  if (!b) return (<><TopBar title={t("events.ticket")} onBack={goBack} /><main className="page page--no-nav"><EmptyState art="generic" title={t("events.noTicket")} /></main></>);
  return (
    <>
      <TopBar title={t("events.ticket")} onBack={goBack} />
      <main className="page page--no-nav">
        <div className="ticket">
          <div className="ticket__top">
            <span className="eyebrow" style={{ color: "#bacadd" }}>KMS ROASTING</span>
            <h3>{b.event.title}</h3>
            <div>{when(b.event.startsAt, locale)} · {b.event.durationMin} {t("events.min")}</div>
          </div>
          <div className="ticket__info">📍 {b.event.place}<br />👤 {b.fullName} · {t("events.seats", { n: b.seats })}</div>
          <div className="ticket__cut" />
          <div className="ticket__qr">
            {qr ? <img src={qr} alt="QR" width={120} height={120} /> : <b style={{ fontSize: 22, letterSpacing: 2 }}>{b.code}</b>}
            <div><b>{t("events.showAtDoor")}</b><br />{t("events.reminder")}</div>
          </div>
        </div>
        <button className="btn btn--danger-text" style={{ width: "100%", marginTop: 16 }} onClick={async () => { await cancelBooking(b.id).catch(() => {}); navigate({ name: "events" }); }}>
          {t("events.cancel")}
        </button>
      </main>
    </>
  );
}
